<template>
  <div class="cancel-order-details">
    <div v-if="cancelDetails" class="d-flex flex-column gap-3">
      <RegionMismatchAdvisory v-if="hasRegionMismatch" />

      <!-- Totals exist for legacy multi-statement orders only: cancellations are split
           into one order per statement at creation, so single-statement is the norm. -->
      <div v-if="isMultiStatementOrder" class="ledger-box">
        <div class="ledger-pair">
          <div class="ledger-cell">
            <div class="eyebrow-label eyebrow-label--sm mb-1">
              {{ $t("company_orders.cancel.total_mwh_cancelled") }}
            </div>
            <div class="ledger-figure">
              <span>{{ totalMwhCancelled }}</span>
              <span class="ledger-figure-unit">MWh</span>
            </div>
          </div>

          <div class="ledger-cell">
            <div class="eyebrow-label eyebrow-label--sm mb-1">
              {{ $t("company_orders.cancel.total_statements") }}
            </div>
            <div class="fw-semibold">{{ cancelDetails.statements.length }}</div>
          </div>
        </div>
      </div>

      <div
        v-for="(statement, index) in cancelDetails.statements"
        :key="statement.temp_id || index"
      >
        <div v-if="isMultiStatementOrder" class="eyebrow-label mb-2">
          {{ $t("company_orders.cancel.statement") }} {{ index + 1 }}
        </div>

        <div class="ledger-box">
          <div class="ledger-grid">
            <div class="ledger-cell">
              <div class="eyebrow-label eyebrow-label--sm mb-1">
                {{ $t("go_cancel.form.mwh_cancelled.short_label") }}
              </div>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <div class="ledger-figure">
                  <span>{{ statement.mwh_cancelled || 0 }}</span>
                  <span class="ledger-figure-unit">MWh</span>
                </div>
                <PrivateStatementPill
                  v-if="isStatementPrivate(statement.grexel_options)"
                />
              </div>
            </div>

            <div class="ledger-cell">
              <div class="eyebrow-label eyebrow-label--sm mb-1">
                {{ $t("go_cancel.form.beneficiary_name.label") }}
              </div>
              <div class="fw-semibold">{{ statement.beneficiary_name }}</div>
              <TgeStatementOptionsSummary :options="statement.tge_options" />
            </div>

            <div class="ledger-cell">
              <div class="eyebrow-label eyebrow-label--sm mb-1">
                {{ $t("go_cancel.form.beneficiary_type.label") }}
              </div>
              <div class="fw-semibold">
                {{ formatBeneficiaryType(statement.beneficiary_type) }}
              </div>
            </div>

            <div class="ledger-cell">
              <div class="eyebrow-label eyebrow-label--sm mb-1">
                {{ $t("go_cancel.form.registry_country_region.label") }}
              </div>
              <div class="fw-semibold">
                {{ getRegistryLabel(statement.registry_country_region, t) }}
              </div>
            </div>

            <!-- Dashed rather than dropped when a statement carries no company ID (the
                 "Any AIB" case), matching CancelGoReviewModal: it keeps the field set
                 identical across cards, and keeps the grid at six cells so it tiles
                 evenly into three, two and one columns. -->
            <div class="ledger-cell">
              <div class="eyebrow-label eyebrow-label--sm mb-1">
                {{ $t("go_cancel.form.registry_code.short_label") }}
              </div>
              <div class="ledger-mono">
                {{ statement.registry_code || "-" }}
              </div>
            </div>

            <div class="ledger-cell">
              <div class="eyebrow-label eyebrow-label--sm mb-1">
                {{ $t("company_orders.cancel.period") }}
              </div>
              <div class="fw-semibold">
                {{ formatStatementPeriod(statement) }}
              </div>
            </div>
          </div>

          <div class="ledger-cell">
            <div class="eyebrow-label eyebrow-label--sm mb-1">
              {{ $t("company_orders.cancel.address") }}
            </div>
            <div class="fw-semibold">{{ statement.address || "-" }}</div>
          </div>

          <div class="ledger-cell">
            <div class="eyebrow-label eyebrow-label--sm mb-1">
              {{ $t("company_orders.cancel.purpose") }}
            </div>
            <div class="ledger-prose">
              {{ formatPurpose(statement.cancellation_purpose) }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="cancelDetails.global_operation_notes" class="ledger-box">
        <div class="ledger-cell">
          <div class="eyebrow-label eyebrow-label--sm mb-1">
            {{ $t("go_cancel.form.global_operation_notes.label") }}
          </div>
          <div class="ledger-prose">
            {{ cancelDetails.global_operation_notes }}
          </div>
        </div>
      </div>
    </div>

    <!-- Fallback to generic display -->
    <GenericOrderDetailsSection v-else :details="details" />
  </div>
</template>

<script setup lang="ts">
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import PrivateStatementPill from "@/dashboard/inventory_management/cancel/PrivateStatementPill.vue";
import TgeStatementOptionsSummary from "@/dashboard/inventory_management/cancel/TgeStatementOptionsSummary.vue";
import RegionMismatchAdvisory from "@/dashboard/inventory_management/cancel/RegionMismatchAdvisory.vue";
import { isStatementPrivate } from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import GenericOrderDetailsSection from "@/dashboard/inventory_management/company_orders/details/GenericOrderDetailsSection.vue";
import { getRegistryLabel } from "@/utils/registries";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { CancelOrderDetails } from "../common/companyOrderTypes";

interface Props {
  details: Record<string, any> | null | undefined;
  totalSelectedMwh: number;
}

const props = defineProps<Props>();

// Composables
const { t } = useI18n();

// Computed
const cancelDetails = computed((): CancelOrderDetails | null => {
  if (!props.details) return null;

  // Type guard to check if details match CancelOrderDetails structure
  if (
    Array.isArray(props.details.statements) &&
    Array.isArray(props.details.portfolio_items)
  ) {
    return props.details as CancelOrderDetails;
  }

  return null;
});

const totalMwhCancelled = computed(() => props.totalSelectedMwh);

const hasRegionMismatch = computed(
  () => cancelDetails.value?.has_region_mismatch === true
);

/**
 * Cancellations created today are split one-per-statement, so both the totals block and the
 * per-card numbering only earn their place on the handful of pre-split orders.
 */
const isMultiStatementOrder = computed(
  () => (cancelDetails.value?.statements.length ?? 0) > 1
);

const formatBeneficiaryType = (type: string): string => {
  const key = `go_cancel.form.beneficiary_type.${type}`;
  return t(key) !== key ? t(key) : type;
};

const formatPurpose = (purpose: string): string => {
  return purpose || "-";
};

const formatStatementPeriod = (
  statement: NonNullable<CancelOrderDetails["statements"]>[number]
): string => {
  const start = statement.period_start
    ? formatDateTimeIntoDate(statement.period_start)
    : null;
  const end = statement.period_end
    ? formatDateTimeIntoDate(statement.period_end)
    : null;

  if (start && end) {
    return `${start} – ${end}`;
  }

  return start ?? end ?? "-";
};
</script>

<style scoped lang="scss">
// One bordered card per block with hairline rules between the fields, rather than a grid of
// filled tiles: cancellation purposes run to a couple of hundred characters, so the fields
// they sit beside have to share a continuous baseline instead of floating in their own boxes.
$rule-color: var(--phoenix-border-color-translucent);

.ledger-box {
  overflow: hidden; // clips the grid's rule colour to the rounded corners
  border: 1px solid var(--phoenix-border-color);
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 2px 8px rgba(var(--phoenix-emphasis-color-rgb), 0.03);
}

// The hairlines between fields are the grid's own background showing through 1px gaps. Each
// grid holds a cell count that divides evenly by every column count it takes — six fields per
// statement, two figures in the totals box — so no track is ever left uncovered and no rule
// stops mid-air.
.ledger-grid,
.ledger-pair {
  display: grid;
  gap: 1px;
  background: $rule-color;

  > .ledger-cell {
    background: #fff;
  }
}

.ledger-grid {
  grid-template-columns: 1fr 1fr 1.5fr;
}

.ledger-pair {
  grid-template-columns: 1fr 1fr;
}

// `size="xl"` gives the modal 1140px at xl, 800px between lg and xl, 500px between sm and lg,
// and the viewport below sm. The column counts are the `col-xl-3 col-md-6` this markup used
// before, carried over unchanged — so the 500px step still keeps two columns, as it did.
@media (max-width: 1199.98px) {
  .ledger-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767.98px) {
  .ledger-grid,
  .ledger-pair {
    grid-template-columns: 1fr;
  }
}

// Full-width cells (address, purpose, notes) sit outside the grid and rule themselves off.
.ledger-box > .ledger-cell {
  border-top: 1px solid $rule-color;

  &:first-child {
    border-top: 0;
  }
}

.ledger-cell {
  min-width: 0;
  padding: 0.875rem 1.125rem;
}

.ledger-figure {
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: var(--phoenix-emphasis-color);
}

.ledger-figure-unit {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--phoenix-secondary-color);
}

.ledger-mono {
  font-family: var(--phoenix-font-monospace);
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.ledger-prose {
  font-size: 0.9375rem;
  line-height: 1.55;
  white-space: pre-line;
  overflow-wrap: anywhere;
  text-wrap: pretty;
}
</style>
